/**
 * Global PWA install state — the single source of truth for the
 * `beforeinstallprompt` event and install funnel tracking.
 *
 * WHY A MODULE (not a component hook)?
 * Chrome fires `beforeinstallprompt` ONCE, shortly after the page passes the
 * installability checks — often BEFORE React finishes mounting the shell
 * that renders the banner. If the listener lives inside a component effect,
 * the event is silently missed and the banner never appears (the exact bug
 * reported on desktop). `initPwaInstall()` is therefore called from
 * `src/index.js` BEFORE `root.render()`, captures the event globally and
 * lets any component subscribe afterwards.
 *
 * Tracking (agency requirement — dataLayer ONLY, GTM snippet untouched):
 *   pwa_install_prompt_shown  → banner became visible
 *   pwa_install_click         → user tapped our Install CTA
 *   pwa_installed             → browser confirmed the install (appinstalled)
 *   pwa_install_dismissed     → user tapped "Not now" / close
 */

const DISMISSED_KEY = 'rota_pwa_install_dismissed_at';
const INSTALLED_KEY = 'rota_pwa_installed';

/** Agency spec: a dismissal is remembered for 7 days. */
export const DISMISS_TTL_DAYS = 7;

/** Agency spec + user choice: banner first appears after ~5 seconds. */
export const SHOW_DELAY_MS = 5000;

let deferredPrompt = null;
let appInstalled = false;
let initialized = false;
const listeners = new Set();

// ---------------------------------------------------------------------------
// dataLayer-only event push (deliberately NOT marketingEvent(): the agency
// consumes these raw in GTM; routing them through GA4/Meta too would
// double-count conversions).
// ---------------------------------------------------------------------------
export function pushPwaEvent(event) {
  if (typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event });
  } catch {
    /* dataLayer unavailable — tracking is best-effort */
  }
}

// ---------------------------------------------------------------------------
// Environment helpers
// ---------------------------------------------------------------------------

/** True when already running as an installed app (standalone window). */
export function isStandalone() {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    // iOS exposes a vendor flag instead of display-mode.
    if (window.navigator.standalone === true) return true;
  } catch {
    /* noop */
  }
  return false;
}

/** iPhone / iPad detection (iPadOS 13+ masquerades as Mac with touch). */
export function isIos() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIPad = /Macintosh/i.test(ua) && 'ontouchend' in document;
  return /iPhone|iPad|iPod/i.test(ua) || isIPad;
}

/**
 * Coarse platform bucket used to pick the right manual install steps when
 * the browser never fired `beforeinstallprompt` (engagement heuristics,
 * already-dismissed native prompt, unsupported browser, dev preview...).
 */
export function getInstallPlatform() {
  if (typeof window === 'undefined') return 'generic';
  if (isIos()) return 'ios';
  const ua = window.navigator.userAgent || '';
  const android = /Android/i.test(ua);
  const edge = /Edg\//.test(ua);
  const chrome = /Chrome\//.test(ua) && !edge;
  const firefox = /Firefox\//.test(ua);
  const safari = /Safari\//.test(ua) && !chrome && !edge && !firefox;
  if (android) return chrome || edge ? 'android-chromium' : 'android-other';
  if (edge) return 'edge-desktop';
  if (chrome) return 'chrome-desktop';
  if (safari) return 'safari-mac';
  if (firefox) return 'firefox-desktop';
  return 'generic';
}

/** True on phones/tablets — drives the "on your phone" copy variant. */
export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return isIos() || /Android|Mobile/i.test(window.navigator.userAgent || '');
}

// ---------------------------------------------------------------------------
// Snooze / installed persistence
// ---------------------------------------------------------------------------
export function wasRecentlyDismissed() {
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return true;
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function snoozeInstallPrompt() {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function markInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
  } catch {
    /* private mode */
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export function getInstallState() {
  return { canPrompt: deferredPrompt !== null, installed: appInstalled };
}

function notifyListeners() {
  const state = getInstallState();
  listeners.forEach((cb) => {
    try {
      cb(state);
    } catch {
      /* a broken subscriber must not break the others */
    }
  });
}

/**
 * Subscribe to install-availability changes. The callback fires immediately
 * with the current state (covers the "event fired before mount" case) and
 * again on every change. Returns an unsubscribe function.
 */
export function subscribeInstall(cb) {
  listeners.add(cb);
  cb(getInstallState());
  return () => listeners.delete(cb);
}

/**
 * Capture install events globally. Call ONCE, before React mounts.
 * Safe to call repeatedly (no-ops after the first).
 */
export function initPwaInstall() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppress Chrome's mini-infobar; we surface our own branded banner.
    e.preventDefault();
    deferredPrompt = e;
    notifyListeners();
  });

  window.addEventListener('appinstalled', () => {
    appInstalled = true;
    deferredPrompt = null;
    markInstalled();
    pushPwaEvent('pwa_installed');
    notifyListeners();
  });
}

/**
 * Trigger the REAL OS install sheet from the stored event.
 * Returns 'accepted' | 'dismissed' | 'unavailable' | 'error'.
 * Fires `pwa_install_click` itself so every call-site (banner button,
 * footer link, app-menu link) is counted exactly once.
 */
export async function promptInstall() {
  const evt = deferredPrompt;
  if (!evt) return 'unavailable';
  pushPwaEvent('pwa_install_click');
  try {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === 'accepted') markInstalled();
    return outcome;
  } catch {
    return 'error';
  } finally {
    // The event is single-use; Chrome fires a fresh one when eligible again.
    deferredPrompt = null;
    notifyListeners();
  }
}
